import { Avatar, Box, Button, Flex, Heading, HStack, Icon, IconButton, Image, Input, SimpleGrid, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PiChartBarDuotone, PiCheckCircleDuotone, PiCaretRightBold, PiClockDuotone, PiCompassDuotone, PiSignOutDuotone,
  PiChatCircleDotsDuotone, PiPlusBold, PiMagnifyingGlassBold, PiTrashDuotone, PiUserPlusDuotone, PiUsersThreeDuotone,
  PiUsersFourDuotone, PiScalesDuotone
} from "react-icons/pi";
import {
  deleteVoterGroup,
  DiscoverGroup,
  DiscoverSort,
  FeedPost,
  getDiscoverGroups,
  getGroupsWallFeed,
  getMyGroups,
  getVoterGroups,
  joinDiscoverGroup,
  leaveGroup,
  MyVoterGroup,
  VoterGroup,
  WallPost
} from "@lubbezposrednio-frontend/core";
import WallPostCard from "./WallPostCard";
import { mediaUrl } from "./wallShared";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { FeedSkeleton, CardsGridSkeleton, SkelLine, SkelCircle } from "../../components/skeletons";

type TabKey = "aktywnosc" | "naleze" | "zarzadzam" | "odkrywaj";
const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: "aktywnosc", label: "Aktywność", icon: PiChatCircleDotsDuotone },
  { key: "naleze", label: "Należę", icon: PiUsersThreeDuotone },
  { key: "zarzadzam", label: "Zarządzam", icon: PiUsersFourDuotone },
  { key: "odkrywaj", label: "Odkrywaj", icon: PiCompassDuotone }
];
const SUBTITLE: Record<TabKey, string> = {
  aktywnosc: "Najnowsze ze wszystkich Twoich grup.",
  naleze: "Grupy, do których należysz jako uczestnik.",
  zarzadzam: "Grupy, którymi zarządzasz — użyj ich przy tworzeniu głosowania.",
  odkrywaj: "Publiczne grupy, do których możesz dołączyć."
};

// ── Rail (styl pulpitu) — te same prymitywy co na stronie grupy ──
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string; onAll?: () => void }> = ({ icon, title, color = "#430098", onAll }) => (
  <Flex justify="space-between" align="center" mb="11px" gap="8px">
    <HStack spacing="8px" minW={0}>
      <Icon as={icon} boxSize="18px" color={color} flexShrink={0} />
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}>{title}</Text>
    </HStack>
    {onAll && <Text as="button" onClick={onAll} fontSize="12px" fontWeight="700" color={color} flexShrink={0} _hover={{ textDecoration: "underline" }}>Wszystkie</Text>}
  </Flex>
);
const RailDivider = () => <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />;
const MetaRow: React.FC<{ icon: React.ElementType; color: string; label: string; value: string }> = ({ icon, color, label, value }) => (
  <HStack justify="space-between" spacing="10px">
    <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={icon} boxSize="14px" color={color} flexShrink={0} /><Text fontSize="13px" noOfLines={1}>{label}</Text></HStack>
    <Text fontSize="13px" fontWeight="800" color="#241536" textAlign="right" noOfLines={1} flexShrink={0}>{value}</Text>
  </HStack>
);

// ── Nagłówek: czysty glass (bez gradientowego banera) + pigułki-taby ──
const HubHeader: React.FC<{ tab: TabKey; onTab: (t: TabKey) => void }> = ({ tab, onTab }) => (
  <Box borderRadius="24px" mb="16px" sx={GLASS}>
    <Box px={{ base: "18px", md: "26px" }} pt={{ base: "18px", md: "22px" }} pb="14px">
      <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="14px" direction={{ base: "column", sm: "row" }}>
        <HStack spacing="14px" minW={0}>
          <Flex w={{ base: "50px", md: "56px" }} h={{ base: "50px", md: "56px" }} borderRadius="16px" bg={drgba("#7b2ff7", 0.12)} align="center" justify="center" flexShrink={0}>
            <Icon as={PiUsersThreeDuotone} boxSize="28px" color="#7b2ff7" />
          </Flex>
          <Box minW={0}>
            <Heading fontSize={{ base: "24px", md: "28px" }} fontWeight="900" color="#1b1030" lineHeight="1.05" letterSpacing="-0.02em">Grupy</Heading>
            <Text fontSize="13.5px" color="#6b6480" mt="3px" noOfLines={1}>{SUBTITLE[tab]}</Text>
          </Box>
        </HStack>
        <Button as={Link} to="/organizator/grupy/nowa" leftIcon={<Icon as={PiPlusBold} />} bg="primary" color="white" borderRadius="12px" fontWeight="800" px="18px" size="sm" flexShrink={0} boxShadow="0 12px 26px -12px rgba(67,0,152,0.6)" _hover={{ bg: "#360079", textDecoration: "none" }}>
          Nowa grupa
        </Button>
      </Flex>

      {/* Pigułki-taby */}
      <HStack spacing="3px" mt="16px" pt="12px" borderTop="1px solid rgba(24,12,48,0.07)" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
        {TABS.map(t => {
          const isActive = t.key === tab;
          return (
            <Button
              key={t.key}
              onClick={() => onTab(t.key)}
              leftIcon={<Icon as={t.icon} boxSize="15px" />}
              size="sm"
              borderRadius="full"
              fontWeight="800"
              px="15px"
              color={isActive ? "#7b2ff7" : "#6b6480"}
              bg={isActive ? "rgba(123,47,247,0.1)" : "transparent"}
              boxShadow="none"
              _hover={{ bg: isActive ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }}
              flexShrink={0}
            >
              {t.label}
            </Button>
          );
        })}
      </HStack>
    </Box>
  </Box>
);

const Empty: React.FC<{ icon?: React.ElementType; title: string; text: string; children?: React.ReactNode }> = ({ icon = PiUsersThreeDuotone, title, text, children }) => (
  <Flex direction="column" align="center" justify="center" py="60px" borderRadius="22px" sx={GLASS}>
    <Flex w="58px" h="58px" borderRadius="16px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="14px">
      <Icon as={icon} boxSize="28px" color="primary" />
    </Flex>
    <Text color="#241536" fontWeight="900" fontSize="18px" mb="4px" letterSpacing="-0.01em">{title}</Text>
    <Text color="#6b6480" fontSize="14px" textAlign="center" maxW="380px" mb={children ? "16px" : 0}>{text}</Text>
    {children}
  </Flex>
);

// Pasek: wypełniona szukajka + segmentowe sortowanie (glass)
const FilterBar: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  sorts: { label: string; value: string }[];
  sort: string;
  onSort: (v: string) => void;
}> = ({ value, onChange, placeholder, sorts, sort, onSort }) => (
  <Flex align="center" gap="10px" wrap="wrap" borderRadius="16px" px="14px" py="8px" sx={GLASS}>
    <Icon as={PiMagnifyingGlassBold} color="#a8a2b6" boxSize="17px" flexShrink={0} />
    <Input variant="unstyled" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} fontSize="14px" fontWeight="500" flex="1" minW="120px" _placeholder={{ color: "#a8a2b6" }} />
    <HStack spacing="2px" bg="rgba(24,12,48,0.05)" borderRadius="full" p="3px" flexShrink={0}>
      {sorts.map(s => {
        const active = s.value === sort;
        return (
          <Button
            key={s.value}
            onClick={() => onSort(s.value)}
            size="xs"
            h="27px"
            px="13px"
            borderRadius="full"
            fontWeight="800"
            fontSize="12px"
            bg={active ? "white" : "transparent"}
            color={active ? "primary" : "#6b6480"}
            boxShadow={active ? "0 1px 5px rgba(67,0,152,0.14)" : "none"}
            _hover={{ bg: active ? "white" : "rgba(24,12,48,0.05)" }}
          >
            {s.label}
          </Button>
        );
      })}
    </HStack>
  </Flex>
);

// ── Aktywność (feed) — pojedyncza kolumna (kontekst przeniesiony do railu) ──
const ActivityPanel: React.FC = () => {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    getGroupsWallFeed(null, "groups")
      .then(r => {
        setPosts(r.data.posts ?? []);
        setCursor(r.data.next_cursor ?? null);
        setHasMore(!!r.data.has_more);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const loadMore = () => {
    if (loadingMoreRef.current || !cursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    getGroupsWallFeed(cursor, "groups")
      .then(r => {
        setPosts(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...(r.data.posts ?? []).filter(p => !seen.has(p.id))];
        });
        setCursor(r.data.next_cursor ?? null);
        setHasMore(!!r.data.has_more);
      })
      .catch(() => setHasMore(false))
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [hasMore, cursor]);

  const updatePost = (postId: string, updater: (p: WallPost) => WallPost) =>
    setPosts(prev => prev.map(p => (p.id === postId ? ({ ...updater(p), group: p.group }) : p)));

  if (loading) return <FeedSkeleton count={3} />;
  if (posts.length === 0) return <Empty icon={PiChatCircleDotsDuotone} title="Cicho w Twoich grupach" text="Gdy ktoś coś opublikuje na ścianie grupy, do której należysz, zobaczysz to tutaj." />;

  return (
    <VStack align="stretch" spacing="16px">
      {posts.map(p => (
        <WallPostCard
          key={p.id}
          post={p}
          groupId={p.group?.id ?? ""}
          isModerator={false}
          groupContext={p.group}
          onChange={updater => updatePost(p.id, updater)}
          onRemove={() => setPosts(prev => prev.filter(x => x.id !== p.id))}
        />
      ))}
      {hasMore && (
        <Flex ref={sentinelRef} justify="center" py="20px">
          {loadingMore && <Spinner color="primary" thickness="3px" />}
        </Flex>
      )}
    </VStack>
  );
};

// ── Należę ──
const MyStatusChip: React.FC<{ status: MyVoterGroup["membership_status"] }> = ({ status }) => {
  const pending = status === "pending";
  return (
    <HStack spacing="5px" bg={pending ? drgba("#f59e0b", 0.14) : drgba("#1f9e18", 0.12)} color={pending ? "#b45309" : "#1f9e18"} px="9px" py="3px" borderRadius="full">
      <Icon as={pending ? PiClockDuotone : PiCheckCircleDuotone} boxSize="13px" />
      <Text fontSize="12px" fontWeight="800">{pending ? "Oczekuje potwierdzenia" : "Potwierdzony"}</Text>
    </HStack>
  );
};

const MyGroupsPanel: React.FC = () => {
  const [groups, setGroups] = useState<MyVoterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [leaving, setLeaving] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    getMyGroups().then(r => setGroups(r.data.voter_groups ?? [])).catch(() => undefined).finally(() => setLoading(false));
  }, []);

  const handleLeave = async (g: MyVoterGroup) => {
    setLeaving(g.id);
    try {
      await leaveGroup(g.id);
      setGroups(prev => prev.filter(x => x.id !== g.id));
      toast({ title: `Wyszedłeś z grupy „${g.name}"`, status: "success", duration: 2200, isClosable: true });
    } catch {
      toast({ title: "Nie udało się wyjść z grupy", status: "error", isClosable: true });
    } finally {
      setLeaving(null);
    }
  };

  if (loading) return <CardsGridSkeleton count={4} columns={{ base: 1, md: 2 }} />;
  if (groups.length === 0) return <Empty title="Nie należysz jeszcze do żadnej grupy" text="Dołączysz przez link z zaproszeniem do grupy albo z zakładki „Odkrywaj”." />;

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
      {groups.map(g => {
        const confirmed = g.membership_status === "confirmed";
        const ongoing = g.upcoming_votings.filter(v => v.status === "ongoing").length;
        const planned = g.upcoming_votings.length - ongoing;
        return (
          <Box
            key={g.id}
            {...(confirmed ? { as: Link, to: `/organizator/grupy/${g.id}` } : {})}
            position="relative"
            borderRadius="20px"
            p={{ base: "16px", md: "18px" }}
            sx={GLASS}
            transition={`transform ${EASE}, box-shadow ${EASE}`}
            _hover={confirmed ? { boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)", transform: "translateY(-3px)", textDecoration: "none" } : {}}
          >
            <Flex align="flex-start" gap="13px" mb="11px">
              <Avatar w="50px" h="50px" borderRadius="14px" name={g.name} src={mediaUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="23px" />} />
              <Box minW={0} flex="1">
                <Text fontWeight="800" color="#241536" fontSize="16px" noOfLines={1}>{g.name}</Text>
                <Text fontSize="13px" color="#6b6480" noOfLines={1} mt="2px">Organizuje: {g.organizer_name}</Text>
              </Box>
            </Flex>

            <HStack spacing="8px" mb="12px" wrap="wrap">
              <MyStatusChip status={g.membership_status} />
              <HStack spacing="4px" bg="rgba(24,12,48,0.05)" px="9px" py="3px" borderRadius="full">
                <Icon as={PiUsersThreeDuotone} boxSize="12px" color="#a8a2b6" />
                <Text fontSize="12px" fontWeight="800" color="#6b6480">{g.members_count}</Text>
              </HStack>
            </HStack>

            {(ongoing > 0 || planned > 0 || g.past_votings.length > 0) && (
              <HStack spacing="14px" mb="13px" fontSize="12.5px" color="#6b6480" wrap="wrap">
                {ongoing > 0 && <HStack spacing="5px"><Box w="7px" h="7px" borderRadius="full" bg="#2fce25" /><Text fontWeight="800" color="#1f9e18">{ongoing} trwa</Text></HStack>}
                {planned > 0 && <HStack spacing="5px"><Box w="7px" h="7px" borderRadius="full" bg="#7b2ff7" /><Text fontWeight="600">{planned} wkrótce</Text></HStack>}
                {g.past_votings.length > 0 && <HStack spacing="5px"><Box w="7px" h="7px" borderRadius="full" bg="#cbd5e1" /><Text fontWeight="600">{g.past_votings.length} zakończ.</Text></HStack>}
              </HStack>
            )}

            <Flex align="center" justify="space-between" gap="10px" pt="12px" borderTop="1px solid rgba(24,12,48,0.06)">
              {confirmed ? (
                <HStack spacing="5px" color="primary" fontSize="13px" fontWeight="800"><Text>Otwórz grupę</Text><Icon as={PiCaretRightBold} boxSize="13px" /></HStack>
              ) : (
                <Text fontSize="12.5px" color="#a8a2b6">Czeka na potwierdzenie</Text>
              )}
              <Button onClick={e => { e.preventDefault(); e.stopPropagation(); handleLeave(g); }} isLoading={leaving === g.id} variant="ghost" size="xs" leftIcon={<Icon as={PiSignOutDuotone} />} color="#e51f44" borderRadius="9px" fontWeight="800" flexShrink={0} _hover={{ bg: "rgba(229,31,68,0.06)" }}>
                Wyjdź
              </Button>
            </Flex>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

// ── Zarządzam ──
const InitialsPreview: React.FC<{ group: VoterGroup }> = ({ group }) => {
  const previews = group.member_previews ?? [];
  const extra = group.members_count - previews.length;
  if (previews.length === 0) return null;
  return (
    <HStack spacing="-8px">
      {previews.map((p, i) => (
        <Avatar key={i} size="sm" name={p.initials} getInitials={s => s} src={p.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${p.avatar_url}` : undefined} bg={drgba("#430098", 0.12)} color="primary" border="2px solid white" />
      ))}
      {extra > 0 && (
        <Flex w="32px" h="32px" borderRadius="full" bg="gray.100" color="gray.500" fontSize="11px" fontWeight="800" align="center" justify="center" border="2px solid white">+{extra}</Flex>
      )}
    </HStack>
  );
};

const SORTS: { label: string; value: string }[] = [
  { label: "Najnowsze", value: "newest" },
  { label: "Nazwa A–Z", value: "name" },
  { label: "Najwięcej osób", value: "members" }
];

const ManagedGroupsPanel: React.FC = () => {
  const [groups, setGroups] = useState<VoterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("newest");
  const toast = useToast();

  useEffect(() => {
    getVoterGroups().then(r => setGroups(r.data.voter_groups ?? [])).catch(() => undefined).finally(() => setLoading(false));
  }, []);

  const remove = async (e: React.MouseEvent, g: VoterGroup) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Usunąć grupę „${g.name}"? Tej operacji nie można cofnąć.`)) return;
    try {
      await deleteVoterGroup(g.id);
      setGroups(prev => prev.filter(x => x.id !== g.id));
      toast({ title: "Grupa usunięta", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się usunąć grupy", status: "error", isClosable: true });
    }
  };

  const visible = groups
    .filter(g => g.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name);
      if (sortKey === "members") return b.members_count - a.members_count;
      return 0;
    });

  if (loading) return <CardsGridSkeleton count={4} columns={{ base: 1, md: 2 }} />;
  if (groups.length === 0) {
    return (
      <Empty title="Nie masz jeszcze żadnej grupy" text="Utwórz np. „Zarząd spółki” albo „Radni gminy”.">
        <Button as={Link} to="/organizator/grupy/nowa" leftIcon={<Icon as={PiPlusBold} />} bg="accent" color="white" borderRadius="12px" fontWeight="800" _hover={{ bg: "#c41a3a" }}>Nowa grupa</Button>
      </Empty>
    );
  }

  return (
    <VStack align="stretch" spacing="16px">
      {groups.length > 1 && (
        <FilterBar value={query} onChange={setQuery} placeholder="Szukaj grupy" sorts={SORTS} sort={sortKey} onSort={setSortKey} />
      )}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
        {visible.map(g => (
          <Box key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} position="relative" borderRadius="20px" p={{ base: "16px", md: "18px" }} sx={GLASS} transition={`transform ${EASE}, box-shadow ${EASE}`} _hover={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)", transform: "translateY(-3px)", textDecoration: "none" }}>
            <IconButton aria-label="Usuń grupę" icon={<Icon as={PiTrashDuotone} />} size="xs" variant="ghost" color="#c9c3d6" position="absolute" top="10px" right="10px" onClick={e => remove(e, g)} _hover={{ color: "accent", bg: "rgba(229,31,68,0.06)" }} />
            <Flex align="flex-start" gap="13px" mb="12px" pr="24px">
              {g.avatar_url ? (
                <Image src={`${process.env.REACT_APP_BACKEND_URL}${g.avatar_url}`} alt={g.name} w="50px" h="50px" borderRadius="14px" objectFit="cover" flexShrink={0} />
              ) : (
                <Flex w="50px" h="50px" align="center" justify="center" bgGradient="linear(135deg, rgba(67,0,152,0.14), rgba(67,0,152,0.04))" borderRadius="14px" flexShrink={0}>
                  <Icon as={PiUsersThreeDuotone} boxSize="23px" color="primary" />
                </Flex>
              )}
              <Box minW={0} flex="1">
                <Heading fontSize="16px" color="#241536" noOfLines={1}>{g.name}</Heading>
                <Text fontSize="13px" color="#6b6480" noOfLines={1} mt="2px">{g.description || "Bez opisu"}</Text>
              </Box>
            </Flex>

            {g.signup_open && (
              <HStack spacing="5px" w="fit-content" bg={drgba("#1f9e18", 0.12)} px="9px" py="3px" borderRadius="full" mb="10px">
                <Box w="6px" h="6px" borderRadius="full" bg="#2FCE25" />
                <Text fontSize="11px" color="#1f9e18" fontWeight="800" whiteSpace="nowrap">Zapisy otwarte</Text>
              </HStack>
            )}

            <Flex align="center" justify="space-between" gap="10px" pt="13px" borderTop="1px solid rgba(24,12,48,0.06)">
              <HStack spacing="9px" minW={0}>
                <InitialsPreview group={g} />
                <HStack spacing="4px">
                  <Text fontSize="14px" fontWeight="800" color="#2f2a3d">{g.members_count}</Text>
                  <Text fontSize="13px" color="#a8a2b6" fontWeight="600">{g.members_count === 1 ? "osoba" : "osób"}</Text>
                </HStack>
              </HStack>
              {(g.pending_count ?? 0) > 0 ? (
                <Text fontSize="11.5px" color="#b7791f" fontWeight="800" bg={drgba("#f59e0b", 0.14)} px="9px" py="2px" borderRadius="full" flexShrink={0}>{g.pending_count} oczekuje</Text>
              ) : (
                <Icon as={PiCaretRightBold} color="#c9c3d6" boxSize="15px" flexShrink={0} />
              )}
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

// ── Odkrywaj ──
const DiscoverGroupCard: React.FC<{ g: DiscoverGroup }> = ({ g }) => {
  const [state, setState] = useState<"none" | "member" | "pending">(g.is_member ? "member" : "none");
  const [count, setCount] = useState(g.members_count);
  const [busy, setBusy] = useState(false);

  const join = async () => {
    setBusy(true);
    try {
      const r = await joinDiscoverGroup(g.id);
      setState(r.data.status === "member" ? "member" : "pending");
      if (r.data.status === "member") setCount(c => c + 1);
    } catch {
      /* ciche */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box borderRadius="20px" p={{ base: "16px", md: "18px" }} sx={GLASS}>
      <Flex align="center" gap="13px" mb="14px">
        <Avatar w="50px" h="50px" borderRadius="14px" name={g.name} src={mediaUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="23px" />} />
        <Box minW={0} flex="1">
          <Text fontWeight="800" color="#241536" fontSize="16px" noOfLines={1}>{g.name}</Text>
          <Text fontSize="13px" color="#6b6480" noOfLines={1} mt="2px">{count} {count === 1 ? "członek" : "członków"}{g.requires_approval ? " · akceptacja" : ""}</Text>
        </Box>
      </Flex>
      {state === "member" ? (
        <Button w="100%" size="sm" variant="outline" borderColor="rgba(67,0,152,0.2)" color="primary" borderRadius="11px" fontWeight="800" leftIcon={<Icon as={PiCheckCircleDuotone} />} as={Link} to={`/organizator/grupy/${g.id}`}>
          Należysz — otwórz
        </Button>
      ) : state === "pending" ? (
        <Button w="100%" size="sm" variant="outline" borderColor="rgba(214,158,46,0.4)" color="#b7791f" borderRadius="11px" fontWeight="800" leftIcon={<Icon as={PiClockDuotone} />} isDisabled>
          Oczekuje akceptacji
        </Button>
      ) : (
        <Button w="100%" size="sm" bg="accent" color="white" borderRadius="11px" fontWeight="800" leftIcon={<Icon as={PiUserPlusDuotone} />} isLoading={busy} onClick={join} _hover={{ bg: "#c41a3a" }}>
          Dołącz
        </Button>
      )}
    </Box>
  );
};

const DiscoverGroupsPanel: React.FC = () => {
  const [groups, setGroups] = useState<DiscoverGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState<DiscoverSort>("popular");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setLoading(true);
    getDiscoverGroups({ q: debounced || undefined, sort })
      .then(r => setGroups(r.data.groups ?? []))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [debounced, sort]);

  return (
    <VStack align="stretch" spacing="16px">
      <FilterBar
        value={query}
        onChange={setQuery}
        placeholder="Szukaj grup"
        sorts={[{ label: "Popularne", value: "popular" }, { label: "Najnowsze", value: "new" }]}
        sort={sort}
        onSort={v => setSort(v as DiscoverSort)}
      />
      {loading ? (
        <CardsGridSkeleton count={4} columns={{ base: 1, md: 2 }} />
      ) : groups.length === 0 ? (
        <Empty icon={PiCompassDuotone} title="Brak publicznych grup" text="Nie znaleźliśmy publicznych grup pasujących do wyszukiwania." />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
          {groups.map(g => <DiscoverGroupCard key={g.id} g={g} />)}
        </SimpleGrid>
      )}
    </VStack>
  );
};

// ── Poziomy pasek szybkiego dostępu: okrągłe awatary + skrócone nazwy grup ──
const QuickGroupsStrip: React.FC<{ groups: VoterGroup[]; loading: boolean }> = ({ groups, loading }) => (
  <Box borderRadius="20px" mb="16px" px={{ base: "14px", md: "18px" }} py="14px" sx={GLASS}>
    <HStack spacing={{ base: "14px", md: "18px" }} align="flex-start" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
      {/* Nowa grupa */}
      <VStack as={Link} to="/organizator/grupy/nowa" spacing="7px" flexShrink={0} w="60px" _hover={{ textDecoration: "none" }}>
        <Flex w="54px" h="54px" borderRadius="full" align="center" justify="center" border="2px dashed rgba(67,0,152,0.3)" bg={drgba("#430098", 0.04)} transition={`all ${EASE}`} _hover={{ bg: drgba("#430098", 0.08), borderColor: "rgba(67,0,152,0.5)" }}>
          <Icon as={PiPlusBold} boxSize="20px" color="primary" />
        </Flex>
        <Text fontSize="11.5px" fontWeight="700" color="#6b6480" noOfLines={1} textAlign="center" w="60px">Nowa</Text>
      </VStack>

      {loading
        ? Array.from({ length: 6 }).map((_, i) => (
            <VStack key={i} spacing="7px" flexShrink={0} w="60px"><SkelCircle size="54px" /><SkelLine w="46px" h="10px" /></VStack>
          ))
        : groups.map(g => (
            <VStack key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} spacing="7px" flexShrink={0} w="60px" _hover={{ textDecoration: "none" }}>
              <Avatar boxSize="54px" borderRadius="full" name={g.name} src={mediaUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="24px" />} transition={`transform ${EASE}`} _hover={{ transform: "translateY(-2px)" }} />
              <Text fontSize="11.5px" fontWeight="700" color="#3f3a4d" noOfLines={1} textAlign="center" w="60px">{g.name}</Text>
            </VStack>
          ))}
    </HStack>
  </Box>
);

// ── Rail: podsumowanie + skróty do zarządzanych grup + szybkie akcje ──
const HubRail: React.FC<{ onTab: (t: TabKey) => void; groups: VoterGroup[]; loading: boolean }> = ({ onTab, groups: managed, loading }) => {
  const navigate = useNavigate();

  const totalMembers = managed.reduce((s, g) => s + g.members_count, 0);
  const openSignups = managed.filter(g => g.signup_open).length;

  return (
    <>
      {/* Podsumowanie */}
      <RailHeading icon={PiUsersFourDuotone} title="Twoje grupy" color="#7b2ff7" onAll={() => onTab("zarzadzam")} />
      <VStack align="stretch" spacing="10px">
        <MetaRow icon={PiUsersFourDuotone} color="#7b2ff7" label="Zarządzasz" value={loading ? "—" : String(managed.length)} />
        <MetaRow icon={PiUsersThreeDuotone} color="#2563eb" label="Łącznie członków" value={loading ? "—" : String(totalMembers)} />
        {openSignups > 0 && <MetaRow icon={PiUserPlusDuotone} color="#10b981" label="Otwarte zapisy" value={String(openSignups)} />}
      </VStack>

      <RailDivider />

      {/* Skróty do zarządzanych grup */}
      <RailHeading icon={PiChartBarDuotone} title="Skróty" color="#2563eb" onAll={() => onTab("zarzadzam")} />
      {loading ? (
        <VStack align="stretch" spacing="10px">
          {Array.from({ length: 4 }).map((_, i) => (
            <HStack key={i} spacing="10px"><SkelCircle size="34px" /><Box flex="1"><SkelLine w="60%" h="12px" /><SkelLine w="35%" h="10px" mt="6px" /></Box></HStack>
          ))}
        </VStack>
      ) : managed.length === 0 ? (
        <Text fontSize="13px" color="#a8a2b6">Nie masz jeszcze żadnej grupy.</Text>
      ) : (
        <VStack align="stretch" spacing="1px">
          {managed.slice(0, 6).map(g => (
            <HStack key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} spacing="10px" py="6px" px="6px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
              <Avatar size="sm" boxSize="34px" borderRadius="10px" name={g.name} src={mediaUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="16px" />} />
              <Box minW={0} flex="1">
                <Text fontSize="13.5px" fontWeight="700" color="#2f2a3d" noOfLines={1}>{g.name}</Text>
                <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600">{g.members_count} {g.members_count === 1 ? "członek" : "członków"}</Text>
              </Box>
              <Icon as={PiCaretRightBold} boxSize="13px" color="#c9c3d6" flexShrink={0} />
            </HStack>
          ))}
        </VStack>
      )}

      <RailDivider />

      {/* Szybkie akcje */}
      <RailHeading icon={PiPlusBold} title="Szybkie akcje" color="#e51f44" />
      <VStack align="stretch" spacing="8px">
        <Button as={Link} to="/organizator/grupy/nowa" justifyContent="flex-start" leftIcon={<Icon as={PiUsersFourDuotone} boxSize="17px" color="#7b2ff7" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: drgba("#430098", 0.05), textDecoration: "none" }}>Nowa grupa</Button>
        <Button onClick={() => navigate("/organizator/utworz-glosowanie")} justifyContent="flex-start" leftIcon={<Icon as={PiScalesDuotone} boxSize="17px" color="#e51f44" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: "rgba(229,31,68,0.05)" }}>Utwórz głosowanie</Button>
        <Button onClick={() => onTab("odkrywaj")} justifyContent="flex-start" leftIcon={<Icon as={PiCompassDuotone} boxSize="17px" color="#2563eb" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: drgba("#2563eb", 0.06) }}>Odkrywaj grupy</Button>
      </VStack>
    </>
  );
};

// ── Hub ──
const GroupsHubView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabKey = (TABS.find(t => t.key === searchParams.get("w"))?.key ?? "aktywnosc") as TabKey;
  const setTab = (t: TabKey) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (t === "aktywnosc") p.delete("w");
      else p.set("w", t);
      return p;
    }, { replace: true });

  const [visited, setVisited] = useState<Set<TabKey>>(() => new Set([tab]));
  useEffect(() => {
    setVisited(v => (v.has(tab) ? v : new Set(v).add(tab)));
  }, [tab]);

  // Grupy zarządzane — jeden fetch dla paska szybkiego dostępu i raila.
  const [managedGroups, setManagedGroups] = useState<VoterGroup[]>([]);
  const [managedLoading, setManagedLoading] = useState(true);
  useEffect(() => {
    getVoterGroups().then(r => setManagedGroups(r.data.voter_groups ?? [])).catch(() => undefined).finally(() => setManagedLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Grupy";
  }, []);

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
          <HubHeader tab={tab} onTab={setTab} />
          {(managedLoading || managedGroups.length > 0) && <QuickGroupsStrip groups={managedGroups} loading={managedLoading} />}
          <Box>
            {visited.has("aktywnosc") && <Box display={tab === "aktywnosc" ? "block" : "none"}><ActivityPanel /></Box>}
            {visited.has("naleze") && <Box display={tab === "naleze" ? "block" : "none"}><MyGroupsPanel /></Box>}
            {visited.has("zarzadzam") && <Box display={tab === "zarzadzam" ? "block" : "none"}><ManagedGroupsPanel /></Box>}
            {visited.has("odkrywaj") && <Box display={tab === "odkrywaj" ? "block" : "none"}><DiscoverGroupsPanel /></Box>}
          </Box>

          {/* Rail zwinięty <xl */}
          <Box display={{ base: "block", xl: "none" }} mt="18px" borderRadius="22px" px="18px" py="20px" sx={GLASS}>
            <HubRail onTab={setTab} groups={managedGroups} loading={managedLoading} />
          </Box>
        </Box>
      </Box>

      {/* ── GŁÓWNY PRAWY RAIL — pływający, pełnowysokościowy (jak na stronie grupy) ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="16px" py="20px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              <HubRail onTab={setTab} groups={managedGroups} loading={managedLoading} />
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default GroupsHubView;
